import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MapPin, Globe, Instagram, Mail, Briefcase } from "lucide-react";
import { API, useAuth } from "@/App";
import Navbar from "@/components/layout/Navbar";
import DashboardHeader from "@/components/layout/DashboardHeader";
import Footer from "@/components/layout/Footer";

const BusinessDetail = () => {
  const { businessId } = useParams();
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/businesses/${businessId}`)
      .then((r) => setBusiness(r.data))
      .catch(() => setBusiness(null))
      .finally(() => setLoading(false));
  }, [businessId]);

  if (loading)
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="container-main py-20 text-center text-white">Loading…</div>
      </div>
    );

  if (!business)
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="container-main py-20 text-center text-white">
          <p className="mb-4">Business not found.</p>
          <Link to="/businesses" className="text-[#5170FF] hover:underline">
            ← Back to directory
          </Link>
        </div>
      </div>
    );

  const showContact = !!user;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="business-detail-page">
      <Navbar />
      <DashboardHeader />

      {/* Hero */}
      <section className="pt-12 md:pt-16 pb-10">
        <div className="container-main">
          <Link
            to="/businesses"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Back to directory
          </Link>

          {business.cover_url && (
            <div className="w-full aspect-[16/6] overflow-hidden mb-8 border-2 border-white">
              <img src={business.cover_url} alt={`${business.name} cover`} className="w-full h-full object-cover" />
            </div>
          )}

          <div className="flex flex-col md:flex-row md:items-start gap-8">
            {/* Logo (square + white border) */}
            <div className="w-32 h-32 flex-shrink-0 border-2 border-white overflow-hidden">
              {business.logo_url ? (
                <img src={business.logo_url} alt={business.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-[#5170FF] flex items-center justify-center text-white text-4xl font-display font-bold">
                  {business.name?.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="overline text-[#5170FF] mb-3 inline-flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Business
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-[#5170FF] leading-[1.05] mb-3 break-words">
                {business.name}
              </h1>
              {business.owner_name && (
                <Link
                  to={`/profile/${business.owner_id}`}
                  className="text-sm text-white/70 hover:text-white inline-block mb-4"
                >
                  by <span className="font-bold underline">{business.owner_name}</span>
                </Link>
              )}

              {(business.location || business.industry) && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#FF2D87] mb-4">
                  {business.location && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-4 h-4" /> {business.location}
                    </span>
                  )}
                  {business.industry && <span className="text-white/70">{business.industry}</span>}
                </div>
              )}

              {business.tagline && (
                <p className="text-xl md:text-2xl font-display text-white mb-4 leading-snug">{business.tagline}</p>
              )}
              {business.description && (
                <p className="text-base text-white/80 leading-relaxed whitespace-pre-wrap mb-6">
                  {business.description}
                </p>
              )}

              {business.tags && business.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-8">
                  {business.tags.map((t) => (
                    <span
                      key={t}
                      className="px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full bg-[#FFC2E8] text-[#0A0A0A]"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}

              {/* Connect — pink-outlined icons (mirrors personal profile but inverted color) */}
              {showContact && (business.website || business.instagram) ? (
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/60 mb-3">Connect</p>
                  <div className="flex gap-3 flex-wrap">
                    {business.website && (
                      <a
                        href={business.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full border-2 border-[#FF2D87] flex items-center justify-center text-[#FF2D87] hover:bg-[#FF2D87] hover:text-white transition-all"
                      >
                        <Globe className="w-5 h-5" />
                      </a>
                    )}
                    {business.instagram && (
                      <a
                        href={`https://instagram.com/${business.instagram.replace("@", "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full border-2 border-[#FF2D87] flex items-center justify-center text-[#FF2D87] hover:bg-[#FF2D87] hover:text-white transition-all"
                      >
                        <Instagram className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>
              ) : !showContact ? (
                <div className="flex items-center gap-3 bg-[#1A1A1A] border border-dashed border-[#5170FF]/40 rounded-lg px-4 py-4 max-w-lg">
                  <Mail className="w-5 h-5 text-[#5170FF]" />
                  <div>
                    <p className="text-white text-sm font-bold">Sign in to view contact info</p>
                    <Link to="/login" className="text-[#5170FF] text-xs underline">
                      Log in
                    </Link>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BusinessDetail;
